import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getIncidencia, updateIncidencia, getComentarios, createComentario, getHistorialIncidencia, valorarIncidencia, getProveedores, asignarProveedor } from '../services/api';
import { useToast } from '../components/Toast';
import IncidentTimeline from '../components/IncidentTimeline';
import './IncidenciaDetalle.css';

function IncidenciaDetalle({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [incidencia, setIncidencia] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [visibilidadComentario, setVisibilidadComentario] = useState('');
  const [editando, setEditando] = useState(false);
  const [formData, setFormData] = useState({});
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comentarioValoracion, setComentarioValoracion] = useState('');
  const [showGracias, setShowGracias] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState('');

  useEffect(() => {
    cargarDatos();
  }, [id]);

  useEffect(() => {
    if (user?.rol === 'administrador') {
      setVisibilidadComentario('');
    } else {
      setVisibilidadComentario('comunidad');
    }
  }, [user?.rol]);

  const cargarDatos = async () => {
    try {
      const [incRes, comRes, histRes] = await Promise.all([
        getIncidencia(id),
        getComentarios(id),
        getHistorialIncidencia(id)
      ]);
      console.log('[DEBUG] GET después de guardar - estado:', incRes.data?.estado);
      setIncidencia(incRes.data);
      setFormData(incRes.data);
      setComentarios(comRes.data);
      setHistorial(histRes.data);
      setProveedorSeleccionado(incRes.data.proveedor_id || '');
      // Cargar proveedores de la comunidad solo si tiene permiso
      if (incRes.data.comunidad_id && ['administrador', 'presidente'].includes(user?.rol)) {
        try {
          const provRes = await getProveedores(incRes.data.comunidad_id);
          setProveedores(provRes.data);
        } catch (e) { console.error('Error cargando proveedores:', e); }
      }
    } catch (error) {
      console.error('Error:', error);
      navigate('/incidencias');
    }
  };

  const handleActualizar = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '' && key !== 'id') {
          data.append(key, formData[key]);
        }
      });
      console.log('[DEBUG] Estado que se envía:', formData.estado);
      const res = await updateIncidencia(id, data);
      console.log('[DEBUG] Respuesta PUT - estado devuelto:', res.data?.estado);
      // Actualizar estado inmediatamente desde la respuesta del PUT
      if (res.data) {
        setIncidencia(res.data);
        setFormData(res.data);
      }
      toast.success(`Actualizado: ${res.data?.estado || 'ok'}`);
      setEditando(false);
      await cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar la incidencia');
      console.error('Error al actualizar:', error);
    }
  };

  const handleComentario = async (e) => {
    e.preventDefault();
    if (!nuevoComentario.trim()) return;
    if (user.rol === 'administrador' && !visibilidadComentario) {
      toast.error('Selecciona la visibilidad del comentario');
      return;
    }
    try {
      await createComentario(id, nuevoComentario, visibilidadComentario);
      setNuevoComentario('');
      setVisibilidadComentario(user.rol === 'administrador' ? '' : 'comunidad');
      cargarDatos();
    } catch (error) {
      toast.error('Error al crear comentario');
    }
  };

  const handleValoracion = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Por favor selecciona una valoración');
      return;
    }
    try {
      await valorarIncidencia(id, rating, comentarioValoracion);
      setShowGracias(true);
      setTimeout(() => setShowGracias(false), 4000);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar valoración');
    }
  };

  const handleAsignarProveedor = async (provId) => {
    try {
      await asignarProveedor(id, provId || null);
      setProveedorSeleccionado(provId);
      toast.success(provId ? 'Proveedor asignado correctamente' : 'Proveedor desasignado');
      cargarDatos();
    } catch (error) {
      toast.error('Error al asignar proveedor');
    }
  };

  const getInitials = (nombre) => {
    if (!nombre) return 'NA';
    const parts = nombre.trim().split(' ').filter(Boolean);
    const first = parts[0]?.[0] || '';
    const second = parts[1]?.[0] || '';
    return `${first}${second}`.toUpperCase() || 'NA';
  };

  const formatTimestamp = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCambioTexto = (cambio) => {
    if (cambio.tipo_cambio === 'creacion') return 'Incidencia creada';
    if (cambio.tipo_cambio === 'estado') return `Estado cambiado: ${cambio.valor_anterior} -> ${cambio.valor_nuevo}`;
    if (cambio.tipo_cambio === 'prioridad') return `Prioridad cambiada: ${cambio.valor_anterior} -> ${cambio.valor_nuevo}`;
    if (cambio.tipo_cambio === 'asignacion') return `Reasignacion: ${cambio.valor_anterior} -> Usuario #${cambio.valor_nuevo}`;
    if (cambio.tipo_cambio === 'categoria') return `Categoria cambiada: ${cambio.valor_anterior} -> ${cambio.valor_nuevo}`;
    if (cambio.tipo_cambio === 'edicion') return 'Incidencia editada';
    return 'Actualizacion del sistema';
  };

  const timelineItems = useMemo(() => {
    if (!incidencia) return [];
    const items = [];

    const imagenesCreacion = incidencia.imagenes && incidencia.imagenes.length > 0
      ? incidencia.imagenes
      : incidencia.imagen
        ? [incidencia.imagen]
        : [];

    items.push({
      id: 'creacion',
      type: 'creation',
      fecha: incidencia.fecha_creacion,
      usuario_nombre: incidencia.creador_nombre,
      usuario_rol: incidencia.creador_rol,
      descripcion: incidencia.descripcion,
      ubicacion: incidencia.ubicacion,
      imagenes: imagenesCreacion
    });

    historial
      .filter((cambio) => cambio.tipo_cambio !== 'creacion')
      .forEach((cambio) => {
        items.push({
          id: `historial-${cambio.id}`,
          type: 'system',
          fecha: cambio.fecha_cambio,
          cambio
        });
      });

    comentarios.forEach((comentario) => {
      items.push({
        id: `comentario-${comentario.id}`,
        type: 'comment',
        fecha: comentario.fecha_creacion,
        comentario
      });
    });

    return items.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [incidencia, historial, comentarios]);

  if (!incidencia) {
    return (
      <div className="incidencia-detalle">
        <div className="timeline-skeleton"></div>
        <div className="timeline-skeleton"></div>
        <div className="timeline-skeleton"></div>
      </div>
    );
  }

  return (
    <div className="incidencia-detalle">
      <button onClick={() => navigate('/incidencias')} className="btn btn-outline btn-sm incidencia-back">← Volver</button>

      {!editando ? (
        <>
          <div className="incidencia-header">
            <div className="incidencia-header-top">
              <div>
                <h1 className="incidencia-title">{incidencia.titulo}</h1>
                <div className="incidencia-status-row">
                  <span className={`badge badge-${incidencia.estado}`}>{incidencia.estado.replace('_', ' ')}</span>
                  <span className="badge">{incidencia.categoria}</span>
                  <span className={`badge badge-${incidencia.prioridad}`}>{incidencia.prioridad}</span>
                </div>
              </div>
              <div className="incidencia-header-actions">
                <div className="timeline-time">{formatTimestamp(incidencia.fecha_creacion)}</div>
                {(user.rol === 'administrador' || user.rol === 'presidente' || user.rol === 'proveedor' || Number(incidencia.usuario_id) === Number(user.id)) && (
                  <button onClick={() => setEditando(true)} className="btn btn-sm btn-primary incidencia-edit-btn">
                    Editar
                  </button>
                )}
              </div>
            </div>
            <div className="incidencia-meta">
              {incidencia.comunidad_nombre && (
                <div>Comunidad: <strong>{incidencia.comunidad_nombre}</strong></div>
              )}
              <div>Creado por: <strong>{incidencia.creador_nombre}</strong> ({incidencia.creador_piso})</div>
              {incidencia.asignado_nombre && (
                <div>Asignado a: <strong>{incidencia.asignado_nombre}</strong></div>
              )}
              {incidencia.proveedor_nombre && (
                <div>Proveedor: <strong>{incidencia.proveedor_nombre}</strong> ({incidencia.proveedor_servicio}) - Tel: {incidencia.proveedor_telefono}</div>
              )}
            </div>
          </div>

          {/* Timeline de tracking del estado */}
          <IncidentTimeline incidencia={incidencia} historial={historial} />

          <div className="incidencia-header">
            {(user.rol === 'administrador' || user.rol === 'presidente') && (
              <div className={`incidencia-assign-card ${incidencia.prioridad === 'alta' ? 'incidencia-assign-card--high' : ''}`}>
                <h3 className="incidencia-assign-title">
                  {incidencia.prioridad === 'alta' ? 'Asignar Proveedor (Prioridad ALTA)' : 'Asignar Proveedor'}
                </h3>
                <div className="incidencia-assign-row">
                  <select
                    className="form-control"
                    style={{ flex: 1, minWidth: '200px' }}
                    value={proveedorSeleccionado}
                    onChange={(e) => handleAsignarProveedor(e.target.value)}
                  >
                    <option value="">-- Sin proveedor --</option>
                    {proveedores.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} - {p.servicio} {p.servicio?.toLowerCase().includes(incidencia.categoria?.toLowerCase()) ? 'OK' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {proveedores.filter(p => p.servicio?.toLowerCase().includes(incidencia.categoria?.toLowerCase())).length > 0 && !proveedorSeleccionado && (
                  <p className="incidencia-assign-hint">
                    Hay proveedores de "{incidencia.categoria}" disponibles en esta comunidad
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="card">
          <form onSubmit={handleActualizar}>
            <div className="form-group">
              <label className="form-label">Título</label>
              <input type="text" className="form-control" value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Descripción</label>
              <textarea className="form-control" value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} rows="5" />
            </div>
            {(user.rol === 'administrador' || user.rol === 'presidente' || user.rol === 'proveedor') && (
              <>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select className="form-control" value={formData.estado} onChange={(e) => setFormData({...formData, estado: e.target.value})}>
                      <option value="pendiente">Pendiente</option>
                      <option value="en_proceso">En Proceso</option>
                      <option value="resuelta">Resuelta</option>
                    </select>
                  </div>
                  {(user.rol === 'administrador' || user.rol === 'presidente') && (
                    <div className="form-group">
                      <label className="form-label">Prioridad</label>
                      <select className="form-control" value={formData.prioridad} onChange={(e) => setFormData({...formData, prioridad: e.target.value})}>
                        <option value="baja">Baja</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                      </select>
                    </div>
                  )}
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setEditando(false)} className="btn btn-outline">Cancelar</button>
              <button type="submit" className="btn btn-primary">Guardar cambios</button>
            </div>
          </form>
        </div>
      )}

      {/* Valoración para incidencias resueltas */}
      {incidencia.estado === 'resuelta' && Number(incidencia.usuario_id) === Number(user?.id) && !incidencia.rating && (
        <div className="card incidencia-rating-card incidencia-rating-card--new">
          <h2 className="incidencia-rating-title">Valora el Servicio</h2>
          <p className="incidencia-rating-text">
            La incidencia ha sido resuelta. ¿Cómo valorarías el servicio recibido?
          </p>
          <form onSubmit={handleValoracion}>
            <div className="incidencia-rating-stars-wrap">
              <div className="incidencia-rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star)}
                    className="incidencia-star-btn"
                    style={{ transform: star <= (hoverRating || rating) ? 'scale(1.15)' : 'scale(1)' }}
                  >
                    {star <= (hoverRating || rating) ? '⭐' : '☆'}
                  </button>
                ))}
              </div>
              <p className="incidencia-rating-caption">
                {rating === 0 && 'Selecciona tu valoración'}
                {rating === 1 && '😞 Muy insatisfecho'}
                {rating === 2 && '😕 Insatisfecho'}
                {rating === 3 && '😐 Neutral'}
                {rating === 4 && '😊 Satisfecho'}
                {rating === 5 && '🤩 Muy satisfecho'}
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">Comentario (opcional)</label>
              <textarea
                className="form-control"
                value={comentarioValoracion}
                onChange={(e) => setComentarioValoracion(e.target.value)}
                placeholder="¿Algo más que quieras compartir?"
                rows="3"
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Enviar Valoración
            </button>
          </form>
        </div>
      )}

      {/* Mostrar valoración existente */}
      {incidencia.rating && (
        <div className="card incidencia-rating-card incidencia-rating-card--done">
          <h2 className="incidencia-rating-title">Valoración del Servicio</h2>
          <div className="incidencia-rating-result">
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} className="incidencia-rating-star-static">
                {star <= incidencia.rating ? '⭐' : '☆'}
              </span>
            ))}
            <span className="incidencia-rating-score">
              {incidencia.rating}/5
            </span>
          </div>
          {incidencia.comentario_valoracion && (
            <p className="incidencia-rating-quote">
              "{incidencia.comentario_valoracion}"
            </p>
          )}
        </div>
      )}

      <div className="incidencia-timeline">
        {timelineItems.map((item) => {
          if (item.type === 'system') {
            return (
              <div key={item.id} className="timeline-item">
                <span className="timeline-dot system"></span>
                <div className="timeline-content system">
                  <div className="timeline-header">
                    <div className="timeline-user">
                      <span className="timeline-name">Evento del sistema</span>
                    </div>
                    <span className="timeline-time">{formatTimestamp(item.fecha)}</span>
                  </div>
                  <p className="timeline-system-text">
                    <span className="timeline-system-highlight">{getCambioTexto(item.cambio)}</span>
                  </p>
                  {item.cambio?.comentario && (
                    <p className="timeline-message">{item.cambio.comentario}</p>
                  )}
                </div>
              </div>
            );
          }

          if (item.type === 'creation') {
            return (
              <div key={item.id} className="timeline-item">
                <span className="timeline-dot"></span>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <div className="timeline-user">
                      <span className="timeline-avatar">{getInitials(item.usuario_nombre)}</span>
                      <div>
                        <div className="timeline-name">{item.usuario_nombre || 'Sistema'}</div>
                        {item.usuario_rol && <span className="timeline-role">{item.usuario_rol}</span>}
                      </div>
                    </div>
                    <span className="timeline-time">{formatTimestamp(item.fecha)}</span>
                  </div>
                  <p className="timeline-system-text">
                    <span className="timeline-system-highlight">Incidencia creada</span>
                  </p>
                  {item.descripcion && (
                    <p className="timeline-message">{item.descripcion}</p>
                  )}
                  {item.ubicacion && (
                    <p className="timeline-location">Ubicacion: {item.ubicacion}</p>
                  )}
                  {item.imagenes && item.imagenes.length > 0 && (
                    <div className="timeline-attachments">
                      {item.imagenes.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Incidencia ${index + 1}`}
                          onClick={() => window.open(img, '_blank')}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          const isOwner = item.comentario?.usuario_id && item.comentario.usuario_id === user?.id;

          return (
            <div key={item.id} className="timeline-item">
              <span className="timeline-dot"></span>
              <div className={`timeline-content ${isOwner ? 'timeline-owner' : ''}`}>
                <div className="timeline-header">
                  <div className="timeline-user">
                    <span className="timeline-avatar">{getInitials(item.comentario?.usuario_nombre)}</span>
                    <div>
                      <div className="timeline-name">{item.comentario?.usuario_nombre}</div>
                      {item.comentario?.usuario_rol && (
                        <span className="timeline-role">{item.comentario.usuario_rol}</span>
                      )}
                    </div>
                    {item.comentario?.visibilidad === 'admin' && (
                      <span className="timeline-visibility">Solo administracion</span>
                    )}
                  </div>
                  <span className="timeline-time">{formatTimestamp(item.fecha)}</span>
                </div>
                <p className="timeline-message">{item.comentario?.comentario}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="incidencia-composer">
        <form onSubmit={handleComentario}>
          <div className="form-group">
            <textarea className="form-control" value={nuevoComentario} onChange={(e) => setNuevoComentario(e.target.value)} placeholder="Escribe un comentario..." rows="3" />
          </div>
          <div className="incidencia-composer-row">
            {user.rol === 'administrador' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Visibilidad</label>
                <select
                  className="form-control"
                  value={visibilidadComentario}
                  onChange={(e) => setVisibilidadComentario(e.target.value)}
                  required
                >
                  <option value="">Selecciona una opcion</option>
                  <option value="comunidad">Visible para vecinos</option>
                  <option value="admin">Solo administracion</option>
                </select>
              </div>
            )}
            <div className="incidencia-composer-actions">
              <button type="submit" className="btn btn-primary btn-sm">Enviar comentario</button>
            </div>
          </div>
        </form>
      </div>

      {/* Popup de agradecimiento tras valoración */}
      {showGracias && (
        <div className="incidencia-thanks-overlay" onClick={() => setShowGracias(false)}>
          <div className="incidencia-thanks-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incidencia-thanks-emoji">🎉</div>
            <h2 className="incidencia-thanks-title">
              ¡Gracias por tu valoración!
            </h2>
            <p className="incidencia-thanks-text">
              Tu opinión nos ayuda a mejorar el servicio de la comunidad.
            </p>
            <button
              onClick={() => setShowGracias(false)}
              className="btn btn-primary incidencia-thanks-btn"
            >
              Aceptar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default IncidenciaDetalle;
